import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, TextField, Button, Paper,
  Alert, CircularProgress
} from '@mui/material';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { calculatePlanetaryPositions, calculateAscendant } from '../utils/ephemeris';
import { lookupCity, geocodeCity, getCityList, getUTCOffset } from '../utils/location';
import { addProfile } from '../services/transitCalendarService';

const AddProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [profileName, setProfileName] = useState('');
  const [birthDate, setBirthDate] = useState(new Date('1990-01-01T12:00:00'));
  const [cityName, setCityName] = useState('');
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [timezone, setTimezone] = useState('UTC');

  // Handle city name change and auto-populate coordinates. A slower answer for what was typed earlier must not overwrite the place
  // ("Dubli" arriving after "Dublin" put a Dublin birth in India, seen 2026-10-31)
  const cityLookup = useRef(0);
  const handleCityChange = async (newCityName) => {
    const lookup = ++cityLookup.current;
    setCityName(newCityName);
    const locationData = lookupCity(newCityName);
    if (locationData) {
      setLatitude(locationData.latitude);
      setLongitude(locationData.longitude);
      setTimezone(locationData.timezone);
      return;
    }
    // Fallback to geocoding API
    if (newCityName.trim().length >= 3) {
      const results = await geocodeCity(newCityName);
      if (lookup !== cityLookup.current) return;
      if (results.length > 0) {
        setLatitude(results[0].latitude);
        setLongitude(results[0].longitude);
        setTimezone(results[0].timezone);
      }
    }
  };

  const handleAddProfile = async (e) => {
    e.preventDefault();

    // Validation
    if (!profileName) {
      setError('Please enter a name for this profile');
      return;
    }

    if (!cityName || latitude === 0) {
      setError('Please select a valid birth location');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Calculate timezone offset
      const utcOffset = getUTCOffset(birthDate, timezone);

      // Convert birth date to UTC
      const year = birthDate.getFullYear();
      const month = birthDate.getMonth();
      const day = birthDate.getDate();
      const hours = birthDate.getHours();
      const minutes = birthDate.getMinutes();
      const seconds = birthDate.getSeconds();
      const birthDateUTC = new Date(Date.UTC(year, month, day, hours, minutes, seconds) - utcOffset * 60 * 60 * 1000);

      // Calculate natal positions
      const natalPlanets = calculatePlanetaryPositions(birthDateUTC);
      const ascendantLongitude = calculateAscendant(birthDateUTC, latitude, longitude);

      // Format natal data for storage
      const natalPositions = {};
      natalPlanets.forEach(planet => {
        natalPositions[planet.name] = {
          sign: planet.sign,
          degree: planet.degreeInSign,
          longitude: planet.longitude,
          isRetrograde: planet.isRetrograde
        };
      });

      // Calculate ascendant sign
      const ZODIAC_SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
                           'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
      const ascendantSignIndex = Math.floor(ascendantLongitude / 30);
      const ascendantSign = ZODIAC_SIGNS[ascendantSignIndex];

      // Add profile via API
      const result = await addProfile({
        profileName,
        // Persist the TRUE UTC birth instant (wall-clock minus birth-location
        // offset), not the picker Date's device-dependent toISOString()
        birthDate: birthDateUTC.toISOString(),
        birthTime: birthDate.toTimeString().split(' ')[0],
        birthLocation: cityName,
        birthLat: latitude,
        birthLon: longitude,
        timezone,
        natalPositions
      });

      // Redirect to profile selection
      navigate('/transit-calendar/profiles');

    } catch (err) {
      setError(err.message || 'Failed to add profile. Please try again.');
      console.error('Add profile error:', err);
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 8
    }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                mb: 3,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Add New Profile
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 4,
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Create a transit calendar for another person (family member, client, etc.)
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleAddProfile}>
              {/* Profile Name */}
              <TextField
                fullWidth
                label="Profile Name"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                required
                sx={{ mb: 3 }}
                helperText="e.g., John Smith, Mom, Client Name"
              />

              {/* Birth Date & Time */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Birth Date & Time
                </Typography>
                <DatePicker
                  selected={birthDate}
                  onChange={(date) => date && setBirthDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minDate={new Date('1900-01-01')}
                  maxDate={new Date()}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  customInput={<TextField fullWidth />}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Exact birth time is required for accurate house calculations
                </Typography>
              </Box>

              {/* Birth Location */}
              <TextField
                fullWidth
                label="Birth Location"
                value={cityName}
                onChange={(e) => handleCityChange(e.target.value)}
                onBlur={(e) => handleCityChange(e.target.value)}
                placeholder="City, State/Country"
                required
                sx={{ mb: 3 }}
                helperText="Start typing to see suggestions"
                inputProps={{
                  list: "city-suggestions-add-profile"
                }}
              />
              <datalist id="city-suggestions-add-profile">
                {getCityList().map(city => (
                  <option key={city} value={city} />
                ))}
              </datalist>

              {/* Show coordinates if location selected */}
              {latitude !== 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location:</strong> {cityName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Coordinates:</strong> {latitude.toFixed(4)}°, {longitude.toFixed(4)}°
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Timezone:</strong> {timezone}
                  </Typography>
                </Box>
              )}

              {/* Buttons */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={() => navigate('/transit-calendar/profiles')}
                  disabled={loading}
                  sx={{
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      borderColor: '#764ba2',
                      bgcolor: 'rgba(102, 126, 234, 0.04)'
                    }
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    }
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      <span>Adding profile...</span>
                    </Box>
                  ) : (
                    'Add Profile'
                  )}
                </Button>
              </Box>
            </form>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AddProfile;
